import { styled } from '@mui/material/styles';
import TableActions from './TableActions';
import { useState } from 'react';
import ContactsImportModal from '@/components/ContactsImportModal/ContactsImportModal';
import ContactsTable from './ContactsTable';
import ContactFormModal from './ContactFormModal';
import MassUpdateModal from './MassUpdateModal';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  deleteUserByUserId,
  deletePartnersUsersByPartnerId,
  fetchFilters,
  fetchPartnersUsersByPartnerId,
  postUserPermissions,
  deleteUserPermissions,
  postResendInvitation,
} from '@/lib/api';
import {
  ApiError,
  ContactSchema,
  PermissionSchema,
} from '@/lib/types';
import { AxiosError } from 'axios';
import { useNotifications } from '@/hooks/useNotifications';
import useHasUserPermissions from '@/hooks/useHasUserPermissions';
import { EDIT_CONTACTS_PERMISSIONS } from '@/constants/Permissions';

const ContactsTab = ({ partnerId }: { partnerId?: string }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [contactFormModalOpen, setContactFormModalOpen] =
    useState(false);
  const [massUpdateModalOpen, setMassUpdateModalOpen] =
    useState(false);
  const [selectedContact, setSelectedContact] = useState<
    ContactSchema | undefined
  >(undefined);
  const [isDeleting, setIsDeleting] = useState(false);
  const canEditContacts = useHasUserPermissions(
    EDIT_CONTACTS_PERMISSIONS,
  );
  // Matches the backend: resending is part of inviting.
  const canResendInvitations = useHasUserPermissions(['users.create']);
  const { showSuccess, showError } = useNotifications();

  const { data: filtersData, isPending: isFiltersPending } = useQuery(
    {
      queryKey: ['filters'],
      queryFn: fetchFilters,
    },
  );

  const {
    data: partnersUsers,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['partnersUsers', partnerId],
    queryFn: () => fetchPartnersUsersByPartnerId(Number(partnerId)),
  });

  const {
    mutateAsync: mutatePostPermissionsAsync,
    isPending: isAddingPermissions,
  } = useMutation({
    mutationFn: postUserPermissions,
    onSuccess: () => {
      refetch();
    },
  });

  const {
    mutateAsync: mutateDeletePermissionsAsync,
    isPending: isDeletingPermissions,
  } = useMutation({
    mutationFn: deleteUserPermissions,
    onSuccess: () => {
      refetch();
    },
  });

  const isUpdating = isAddingPermissions || isDeletingPermissions;

  const filteredPartnersUsers =
    partnersUsers?.filter(
      (user: ContactSchema) =>
        user.name.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email.toLowerCase().includes(searchText.toLowerCase()),
    ) || [];

  const { mutate: deleteUser } = useMutation({
    mutationFn: deleteUserByUserId,
  });

  const { mutate: unlinkUser, isPending: isUnlinking } = useMutation({
    mutationFn: (userId: number) =>
      deletePartnersUsersByPartnerId(Number(partnerId), [userId]),
    onSuccess: () => {
      refetch();
    },
  });

  const {
    mutate: resendInvitation,
    isPending: isResendingInvitation,
    variables: resendingInvitationUserId,
  } = useMutation({
    mutationFn: postResendInvitation,
    onSuccess: () => {
      showSuccess('Invitation resent');
      refetch();
    },
    onError: (error: AxiosError<ApiError>) => {
      // 409: nothing to resend (e.g. the user has already accepted); the
      // server says which.
      showError(
        (error.response?.status === 409 &&
          error.response.data?.error?.message) ||
          'Could not resend the invitation. Please try again.',
      );
    },
  });

  const handleMassUpdateClick = () => {
    setMassUpdateModalOpen(true);
  };

  const handleMassUpdateModalClose = () => {
    setMassUpdateModalOpen(false);
  };

  const handleMassUpdateSave = () => {
    setMassUpdateModalOpen(false);
    setSelectedRows([]);
    refetch();
  };

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(
        selectedRows.map(
          (userId) =>
            new Promise((resolve, reject) => {
              deleteUser(userId, {
                onSuccess: resolve,
                onError: reject,
              });
            }),
        ),
      );
      setSelectedRows([]);
      await refetch();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMassImportClick = () => {
    setImportModalOpen(true);
  };

  const handleImportModalClose = () => {
    setImportModalOpen(false);
  };

  const handleImportSave = () => {
    setImportModalOpen(false);
    refetch();
  };

  const handleAddContactClick = () => {
    setSelectedContact(undefined);
    setContactFormModalOpen(true);
  };

  const handleContactFormModalClose = () => {
    setContactFormModalOpen(false);
    setSelectedContact(undefined);
  };

  const handleContactSaved = () => {
    setContactFormModalOpen(false);
    setSelectedContact(undefined);
    refetch();
  };

  const handleDelete = (userId: number) => {
    deleteUser(userId, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  const handleUnlink = (userId: number) => {
    unlinkUser(userId);
  };

  const handleEdit = (userId: number) => {
    const contact = partnersUsers?.find(
      (user: ContactSchema) => user.id === userId,
    );
    if (contact) {
      setSelectedContact(contact);
      setContactFormModalOpen(true);
    }
  };

  const handlePermissionChange = async (
    userId: number,
    names: string[],
    value: boolean,
  ) => {
    const permission_ids = names
      .map(
        (name) =>
          filtersData?.permissions?.find((p) => p.key === name)?.id,
      )
      .filter(Boolean) as number[];
    const user = partnersUsers?.find(
      (user: ContactSchema) => user.id === userId,
    );
    if (!user) return;

    const currentPermissionIds =
      user.permissions?.map((p: PermissionSchema) => p.id) || [];

    try {
      if (value && permission_ids.length > 0) {
        const permissionsToAdd = permission_ids.filter(
          (id) => !currentPermissionIds.includes(id),
        );
        if (permissionsToAdd.length > 0) {
          await mutatePostPermissionsAsync({
            user_id: userId,
            permission_ids: permissionsToAdd,
          });
        }
      } else if (!value && permission_ids.length > 0) {
        const permissionsToDelete = permission_ids.filter((id) =>
          currentPermissionIds.includes(id),
        );
        if (permissionsToDelete.length > 0) {
          await mutateDeletePermissionsAsync({
            user_id: userId,
            permission_ids: permissionsToDelete,
          });
        }
      }
    } catch (error) {
      console.error('Permission update failed:', error);
    }
  };

  const isLoading =
    isFetching ||
    isDeleting ||
    isUpdating ||
    isUnlinking ||
    isFiltersPending;

  return (
    <>
      <TableActions
        searchText={searchText}
        onSearchTextChange={setSearchText}
        onMassUpdateClick={handleMassUpdateClick}
        massUpdateDisabled={
          selectedRows.length === 0 || !canEditContacts
        }
        onDeleteClick={handleDeleteClick}
        deleteDisabled={selectedRows.length === 0 || !canEditContacts}
        onMassImportClick={handleMassImportClick}
        massImportDisabled={!canEditContacts}
        onAddClick={handleAddContactClick}
        addDisabled={!canEditContacts}
        loading={isLoading}
        addLabel={
          <FormattedMessage
            id="addContact"
            defaultMessage="Add Contact"
          />
        }
      />
      <StyledTable
        loading={isLoading}
        rows={filteredPartnersUsers || []}
        selectedRows={canEditContacts ? selectedRows : undefined}
        onSelectedRowsChange={
          canEditContacts ? setSelectedRows : undefined
        }
        onDelete={canEditContacts ? handleDelete : undefined}
        onPermissionChange={
          canEditContacts ? handlePermissionChange : undefined
        }
        onEdit={canEditContacts ? handleEdit : undefined}
        onUnlink={canEditContacts ? handleUnlink : undefined}
        onResendInvitation={
          canResendInvitations ? resendInvitation : undefined
        }
        resendingInvitationUserId={
          isResendingInvitation ? resendingInvitationUserId : undefined
        }
      />
      <ContactsImportModal
        open={importModalOpen}
        onClose={handleImportModalClose}
        onSave={handleImportSave}
        partnerId={partnerId ? parseInt(partnerId) : undefined}
      />
      {partnerId && (
        <ContactFormModal
          open={contactFormModalOpen}
          onClose={handleContactFormModalClose}
          onSave={handleContactSaved}
          partnerId={parseInt(partnerId)}
          contact={selectedContact}
        />
      )}
      <MassUpdateModal
        open={massUpdateModalOpen}
        onClose={handleMassUpdateModalClose}
        onSave={handleMassUpdateSave}
        selectedUsers={
          partnersUsers?.filter((user: ContactSchema) =>
            selectedRows.includes(user.id),
          ) || []
        }
      />
    </>
  );
};

const StyledTable = styled(ContactsTable)({
  padding: '0 40px',
});

export default ContactsTab;
